import { useState, useCallback, useRef, useEffect } from "react";
import { invokeEdgeFunction, type StreamEventHandler } from "@/lib/fetchJson";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import { useProjectMemory } from "./useProjectMemory";
import { normalizeError } from "@/lib/normalizeError";
import { filterEditsForLanding } from "@/lib/landingFilter";
import { runPreflightValidation, buildFixInstruction, autoFixImports, type ValidationIssue } from "@/lib/preflightValidation";
import { scanForSecrets, type SecretMatch, type ScanResult } from "@/lib/secretScanner";
import { validateMigrationFiles } from "@/lib/rlsValidator";
import { logBuildErrorClient, classifyBuildError } from "@/lib/buildErrorLogger";

export type AIAgentMode = "build" | "explain" | "refactor" | "test" | "fix";

interface FileEdit {
  filePath: string;
  action: "create" | "modify" | "delete";
  content?: string;
  explanation: string;
}

/** Orchestration result from execute-build-plan edge function */
interface OrchestrationFiles {
  attempted: number;
  applied: number;
  failed: number;
  errors: Array<{ path: string; error: string }>;
}

interface OrchestrationMigration {
  status: "applied" | "skipped" | "failed" | "none";
  sql_file?: string;
  tables_created?: number;
  statements_executed?: number;
  error?: string;
  reason?: string;
}

export interface OrchestrationResult {
  files: OrchestrationFiles;
  migration: OrchestrationMigration;
}

interface EditPlan {
  summary: string;
  edits: FileEdit[];
  reasoning: string;
  orchestration?: OrchestrationResult;
}

interface ExplanationResult {
  summary: string;
  overview: string;
  keyComponents: Array<{ name: string; description: string; path: string }>;
  dataFlow: string;
  potentialRisks: string[];
}

interface Message {
  role: "user" | "assistant";
  content: string;
  editPlan?: EditPlan;
  explanation?: ExplanationResult;
  mode?: AIAgentMode;
  timestamp: Date;
}

interface ProjectFile {
  name: string;
  content: string;
}

interface ProjectGraph {
  nodes: any[];
  edges: any[];
  componentTree: any[];
  routeMap: any[];
  dependencies: any[];
  summary: {
    totalFiles: number;
    components: number;
    pages: number;
    hooks: number;
    utils: number;
    framework: string;
  };
}

type BuildPatchAction = "create" | "update" | "delete";
interface BuildPatch {
  path: string;
  action: BuildPatchAction;
  content?: string;
}

/** Lightweight conversation history entry sent to the edge function (role + content only). */
interface ConversationHistoryEntry {
  role: "user" | "assistant";
  content: string;
}

/** Max conversation history messages to send (10 = 5 user/assistant exchanges). */
const MAX_CONVERSATION_HISTORY = 10;

export interface StreamingFileProgress {
  path: string;
  action: string;
  size: number;
  completedAt: number;
  label?: string; // Human-readable label for UI (e.g., "Building Dashboard" instead of "DashboardScreen.tsx")
}

export interface StreamingProgress {
  phase: "idle" | "generating" | "complete";
  completedFiles: StreamingFileProgress[];
  totalExpectedFiles?: number;
  buildResetAt?: number; // Timestamp when clear_files was received — watch this to clear preview
  lastUpdate?: number; // Timestamp to force React re-renders
  // Import validation results
  validationPassed?: boolean;
  validationErrorsFixed?: number;
  validationPasses?: number;
  validationRemaining?: number;
}

export interface PlanPreview {
  message: string;
  // Below fields are optional — edit plans only have message
  industry?: string;
  designSystem?: string;
  totalFiles?: number;
  pages?: string[];
  components?: number;
  files?: Array<{ path: string; type: string; description: string }>;
}

function patchToPlan(data: any): EditPlan {
  // Extract orchestration data if present (from execute-build-plan)
  const orchestration: OrchestrationResult | undefined = data?.orchestration;

  if (data?.plan?.summary && Array.isArray(data?.plan?.edits)) {
    const edits: FileEdit[] = data.plan.edits.map((e: any) => ({
      filePath: e.filePath,
      action: e.action === "create" ? "create" : e.action === "delete" ? "delete" : "modify",
      content: e.content,
      explanation: e.explanation ?? "",
    }));

    return {
      summary: String(data.plan.summary ?? "Generated changes"),
      edits,
      reasoning: String(data.plan.reasoning ?? ""),
      orchestration,
    };
  }

  if (Array.isArray(data?.patch)) {
    const edits: FileEdit[] = (data.patch as BuildPatch[]).map((p) => ({
      filePath: p.path,
      action: p.action === "create" ? "create" : p.action === "delete" ? "delete" : "modify",
      content: p.content,
      explanation: "Generated by build function patch output",
    }));

    // Prefer the conversational "message" field from the AI, fall back to "summary"
    const aiMessage = typeof data.message === "string" && data.message.trim()
      ? data.message.trim()
      : null;

    return {
      summary: aiMessage || String(data.summary ?? `Generated ${edits.length} file edit(s)`),
      edits,
      reasoning: String(data.reasoning ?? "Converted patch output into a plan."),
      orchestration,
    };
  }

  return {
    summary: String(data?.summary ?? "No changes generated"),
    edits: [],
    reasoning: "",
    orchestration,
  };
}

// Helper: Ensure project exists in code_projects before inserting to builder_messages
// This prevents RLS 403 errors from race conditions when project row isn't committed yet
async function ensureProjectExists(projectId: string): Promise<boolean> {
  const { data } = await supabase
    .from("code_projects")
    .select("id")
    .eq("id", projectId)
    .maybeSingle();

  if (data) return true;

  // Project not found, wait 500ms and retry once
  console.log("[useAICodeAgent] Project not found, waiting 500ms before retry...", projectId);
  await new Promise(resolve => setTimeout(resolve, 500));

  const { data: retryData } = await supabase
    .from("code_projects")
    .select("id")
    .eq("id", projectId)
    .maybeSingle();

  if (retryData) {
    console.log("[useAICodeAgent] Project found after retry:", projectId);
    return true;
  }

  console.error("[useAICodeAgent] Project still not found after retry:", projectId);
  return false;
}

const FALLBACK_INDEX_HTML = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>App Preview</title>
    <script src="https://cdn.tailwindcss.com"></script>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>
`;

const FALLBACK_MAIN_TSX = `import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";

const container = document.getElementById("root");
if (container) {
  createRoot(container).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}
`;

export type ServerPatchMeta = {
  rejectedPatchItems: Array<{ path: string; reason: string; index?: number }>;
  acceptedCount: number;
  rejectedCount: number;
  patchTotalChars: number;
  /** Intent targets (filesToTouch + rationale) from model when present */
  intent?: { filesToTouch: string[]; rationale?: string };
  /** Patch items rejected because path not in intent filesToTouch */
  intentRejections?: Array<{ path: string; reason: string }>;
  intentRejectionsCount?: number;
  /** Human-controlled override was enabled for this request */
  allowCoreEdits?: boolean;
  /** Patch items rejected due to core protection (src/lib/, src/components/ui/) */
  coreRejectedCount?: number;
};

export function useAICodeAgent(projectId: string | null = null) {
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesRef = useRef<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<EditPlan | null>(null);
  const [currentMode, setCurrentMode] = useState<AIAgentMode>("build");
  const [lastServerPatchMeta, setLastServerPatchMeta] = useState<ServerPatchMeta | null>(null);
  const [lastRequestFlags, setLastRequestFlags] = useState<{ requestMode: string; ruleChange: boolean } | null>(null);
  const [streamingProgress, setStreamingProgress] = useState<StreamingProgress>({ phase: "idle", completedFiles: [] });
  const [planPreview, setPlanPreview] = useState<PlanPreview | null>(null);
  const [securityAlerts, setSecurityAlerts] = useState<SecretMatch[]>([]);
  const [narrativeMessages, setNarrativeMessages] = useState<Array<{
    text: string;
    step: string;
    ts: number;
    description?: string;
    descriptions?: string[];
    files?: string[];
  }>>([]);
  // Conversation history for multi-turn context (lightweight role/content pairs sent to edge function)
  const conversationHistoryRef = useRef<ConversationHistoryEntry[]>([]);

  // Reset conversation history when projectId changes (new project = fresh context)
  const prevProjectIdRef = useRef<string | null>(projectId);
  useEffect(() => {
    if (projectId !== prevProjectIdRef.current) {
      prevProjectIdRef.current = projectId;
      conversationHistoryRef.current = [];
    }
  }, [projectId]);

  const {
    memory,
    isLoading: isMemoryLoading,
    addPromptToHistory,
    addFeature,
    addArchitectureDecision,
    setAppPurpose,
    updateTechStack,
    buildContextString,
  } = useProjectMemory(projectId);

  const pushMessage = useCallback((m: Message) => {
    messagesRef.current = [...messagesRef.current, m];
    setMessages(messagesRef.current);
  }, []);

  // Persist a message to builder_messages table (fire-and-forget)
  // Includes race condition fix: waits for project to exist before inserting
  const persistMessage = useCallback((m: Message, pId: string, filesChanged?: string[]) => {
    if (!pId) return;

    // Use async IIFE to allow await while keeping fire-and-forget behavior
    (async () => {
      // Wait for project to exist in code_projects (fixes RLS 403 race condition)
      const projectExists = await ensureProjectExists(pId);
      if (!projectExists) {
        console.error("[useAICodeAgent] Cannot persist message - project does not exist:", pId);
        return;
      }

      const row = {
        project_id: pId,
        role: m.role,
        content: m.content,
        files_changed: filesChanged ?? null,
        status: "complete",
      };

      const { error, status } = await supabase.from("builder_messages").insert(row as any);
      if (error) {
        console.error("[useAICodeAgent] Failed to persist message:", error.message, error.code, error.details);
      } else {
        console.log("[useAICodeAgent] Saved message to DB:", m.role, "status:", status);
      }
    })();
  }, []);

  // Load chat history from builder_messages on projectId change
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from("builder_messages")
        .select("role, content, created_at")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true });

      if (cancelled || error || !data || data.length === 0) {
        if (error) console.warn("[useAICodeAgent] Failed to load chat history:", error.message);
        return;
      }

      console.log(`[useAICodeAgent] Restored ${data.length} chat messages from DB for project ${projectId}`);

      const restored: Message[] = data.map((row: any) => ({
        role: row.role as "user" | "assistant",
        content: row.content,
        timestamp: new Date(row.created_at),
      }));

      messagesRef.current = restored;
      setMessages(restored);

      // Also restore conversation history ref so next build has context
      conversationHistoryRef.current = data.map((row: any) => ({
        role: row.role as "user" | "assistant",
        content: row.content,
      }));
    })();

    return () => { cancelled = true; };
  }, [projectId]);

  // ✅ Ensure previewable: index.html + src/main.tsx + src/App.tsx must exist somewhere in the edits
  const ensurePreviewFilesInPlan = useCallback((planIn: EditPlan | null | undefined, existingFilePaths?: Set<string>): EditPlan => {
    const plan: EditPlan = planIn ?? { summary: "", edits: [], reasoning: "" };
    const edits = Array.isArray(plan.edits) ? plan.edits : [];

    // Rule-change only: do not inject preview files when the only edit is .dayone/builder-rules.md
    if (edits.length > 0 && edits.every((e) => e.filePath === ".dayone/builder-rules.md")) {
      return plan;
    }

    const hasIndex = edits.some((e) => e.filePath === "index.html");
    const hasMain = edits.some((e) =>
      ["src/main.tsx", "src/main.jsx"].includes(e.filePath)
    );
    const hasApp = edits.some((e) => {
      const lower = e.filePath.toLowerCase();
      return lower === "src/app.tsx" || lower === "src/app.jsx";
    });
    // Also check if the project already has these files (case-insensitive) — don't overwrite with fallback
    const projectHasApp = existingFilePaths
      ? [...existingFilePaths].some((p) => {
          const lower = p.toLowerCase();
          return lower === "src/app.tsx" || lower === "src/app.jsx";
        })
      : false;

    const injected: FileEdit[] = [];

    if (!hasIndex) {
      injected.push({
        filePath: "index.html",
        action: "create",
        explanation: "Auto-injected index.html so the sandbox has a valid entry HTML.",
        content: FALLBACK_INDEX_HTML,
      });
    }

    if (!hasMain) {
      injected.push({
        filePath: "src/main.tsx",
        action: "create",
        explanation: "Auto-injected main entry so the project mounts React into #root.",
        content: FALLBACK_MAIN_TSX,
      });
    }

    if (injected.length === 0) return plan;

    console.log("[useAICodeAgent] Injected missing preview files:", injected.map((i) => i.filePath).join(", "));

    return {
      ...plan,
      edits: [...injected, ...edits],
      summary: plan.summary || "Changes applied",
      reasoning: plan.reasoning ?? "",
    };
  }, []);

  const sendInstruction = useCallback(
    async (
      instruction: string,
      projId: string,
      files: ProjectFile[],
      projectGraph?: ProjectGraph | null,
      mode: AIAgentMode = "build",
      errorLogs?: string,
      opts?: { filterForLanding?: boolean; onLandingFilterDropped?: (droppedCount: number, samplePaths: string[]) => void; overrideMessage?: string }
    ): Promise<EditPlan | ExplanationResult | null> => {
      if (!instruction.trim() && mode !== "fix") return null;
      if (!projId) {
        toast.error("No active project selected");
        return null;
      }

      const userMessageContent = mode === "fix" && errorLogs ? `Fix these errors:\n${errorLogs}` : instruction;
      const userMessage: Message = {
        role: "user",
        content: userMessageContent,
        mode,
        timestamp: new Date(),
      };

      pushMessage(userMessage);
      persistMessage(userMessage, projId);
      setIsLoading(true);
      let buildSessionId: string | null = null;
      try { buildSessionId = localStorage.getItem("dayone:sandbox:activeSessionId"); } catch { /* ignore */ }

      try {
        const memoryContext = buildContextString();

        const currentFiles = files.map((f) => ({
          path: f.name,
          content: f.content,
        }));

        // allowCoreEdits: token in prompt OR debug toggle in localStorage (client-only, never from model)
        let messageToSend = instruction;
        let allowCoreEdits = false;
        if (/\s*\[allow-core-edits\]\s*/i.test(instruction)) {
          messageToSend = instruction.replace(/\s*\[allow-core-edits\]\s*/gi, " ").trim();
          allowCoreEdits = true;
        }
        if (!allowCoreEdits && typeof localStorage !== "undefined") {
          allowCoreEdits = localStorage.getItem("dayone:allowCoreEdits") === "true";
        }

        // ruleChange: only when user explicitly requests via token [rules] or [edit-builder-rules]
        let ruleChange = false;
        if (/\s*\[(rules|edit-builder-rules)\]\s*/i.test(messageToSend)) {
          messageToSend = messageToSend.replace(/\s*\[(rules|edit-builder-rules)\]\s*/gi, " ").trim();
          ruleChange = true;
        }

        const requestMode = mode === "fix" ? "fix" : ruleChange ? "rules" : "build";
        setLastRequestFlags({ requestMode, ruleChange });

        // Send last N conversation history entries for multi-turn context
        const historyToSend = conversationHistoryRef.current.slice(-MAX_CONVERSATION_HISTORY);

        console.log("[builder-chat-openai] sending", { projectId: projId, allowCoreEdits, ruleChange, requestMode, historyCount: historyToSend.length });

        const edgeFnBody = {
          projectId: projId,
          message: opts?.overrideMessage || messageToSend,
          currentFiles,
          conversationHistory: historyToSend,
          errorLogs,
          buildRunId: null,
          dryRun: false,
          memoryContext,
          mode,
          allowCoreEdits: allowCoreEdits || undefined,
          ruleChange: ruleChange || undefined,
        };

        // Streaming progress: show file_complete events in real-time
        setStreamingProgress({ phase: "generating", completedFiles: [] });
        setPlanPreview(null); // Clear previous plan preview
        // Write initial build state so __render.tsx iframe can show skeleton + progress pill
        if (buildSessionId) {
          try {
            localStorage.setItem(`dayone:sandbox:buildstate:${buildSessionId}`,
              JSON.stringify({ phase: "generating", completed: 0, total: 0, ts: Date.now() }));
          } catch { /* ignore */ }
        }
        const onStreamEvent: StreamEventHandler = (eventType, eventData) => {
          if (eventType === "file_complete") {
            const d = eventData as { path: string; action: string; size: number };
            setStreamingProgress(prev => {
              const next = {
                ...prev,
                completedFiles: [...prev.completedFiles, {
                  path: d.path, action: d.action, size: d.size,
                  completedAt: Date.now(),
                }],
              };
              // Update build state in localStorage for __render.tsx polling
              if (buildSessionId) {
                try {
                  localStorage.setItem(`dayone:sandbox:buildstate:${buildSessionId}`,
                    JSON.stringify({
                      phase: "generating",
                      completed: next.completedFiles.length,
                      total: next.totalExpectedFiles ?? 0,
                      ts: Date.now(),
                    }));
                } catch { /* ignore */ }
              }
              return next;
            });
          } else if (eventType === "progress") {
            const d = eventData as { phase?: string; expectedFiles?: number };
            if (typeof d.expectedFiles === "number" && d.expectedFiles > 0) {
              setStreamingProgress(prev => ({ ...prev, totalExpectedFiles: d.expectedFiles }));
              // Also update the total in buildstate
              if (buildSessionId) {
                try {
                  const raw = localStorage.getItem(`dayone:sandbox:buildstate:${buildSessionId}`);
                  if (raw) {
                    const state = JSON.parse(raw);
                    state.total = d.expectedFiles;
                    state.ts = Date.now();
                    localStorage.setItem(`dayone:sandbox:buildstate:${buildSessionId}`, JSON.stringify(state));
                  }
                } catch { /* ignore */ }
              }
            }
          } else if (eventType === "plan") {
            // PRE-RESPONSE PLAN: Show the user what will be built before generating files
            const d = eventData as PlanPreview;
            setPlanPreview(d);
          } else if (eventType === "clear_files") {
            // BUG 3 FIX: Clear old files when new build starts
            console.log("[useAICodeAgent] clear_files event — resetting streaming progress");
            setStreamingProgress({ phase: "generating", completedFiles: [], buildResetAt: Date.now() });
            setNarrativeMessages([]); // Clear narrative for new build
            // Clear localStorage preview files for this session
            if (buildSessionId) {
              try {
                localStorage.removeItem(`dayone:sandbox:files:${buildSessionId}`);
                localStorage.removeItem(`dayone:sandbox:buildstate:${buildSessionId}`);
              } catch { /* ignore */ }
            }
          } else if (eventType === "narrative") {
            // V3 Streaming narrative - build progress messages
            const d = eventData as {
              text: string;
              step: string;
              description?: string;
              descriptions?: string[];
              files?: string[];
            };
            console.log("[useAICodeAgent] narrative:", d.text);
            setNarrativeMessages(prev => [...prev, {
              text: d.text,
              step: d.step,
              ts: Date.now(),
              description: d.description,
              descriptions: d.descriptions,
              files: d.files,
            }]);
          }
        };

        let data: any;

        // ═══════════════════════════════════════════════════════════════════════════
        // BUILD PATH ROUTING — New builds vs Edits
        // New build = stream-app-build (single-call, server-side Anthropic streaming)
        // Edit = builder-chat-openai (existing SSE path)
        // ═══════════════════════════════════════════════════════════════════════════

        // ═══════════════════════════════════════════════════════════════════════════
        // BUILD PATH DETECTION
        // New build → stream-app-build (single-call, fast)
        // Edit → builder-chat-openai (conversation-based)
        // ═══════════════════════════════════════════════════════════════════════════

        // Check for scaffold placeholder in App.tsx (indicates no real build yet)
        const appFile = currentFiles.find(f => f.path === "src/App.tsx");
        const appContent = appFile?.content || "";
        const isScaffoldPlaceholder = appContent.includes("Building your app...") ||
                                       appContent.includes("animate-spin") && appContent.length < 1000;

        // Files that indicate a real built app (not just scaffold)
        const hasPages = currentFiles.some(f => f.path.startsWith("src/pages/") && !f.path.includes("Landing"));
        const hasScreens = currentFiles.some(f => f.path.startsWith("src/screens/"));
        const hasRealComponents = currentFiles.some(f =>
          f.path.startsWith("src/components/") &&
          !f.path.includes("/ui/") &&
          !["Shell", "Hero", "Features", "Testimonials", "Pricing", "Footer", "Navbar"].some(name =>
            f.path.includes(name)
          )
        );

        // Is this a real built app or just scaffold?
        const hasRealApp = (hasPages || hasScreens || hasRealComponents) && !isScaffoldPlaceholder;

        // ROUTING DECISION:
        // - Scaffold placeholder or no real app → stream-app-build (new build)
        // - Real app exists → builder-chat-openai (edit)
        // - Fix/explain modes → builder-chat-openai
        // NOTE: We no longer check looksLikeNewBuild — if hasRealApp is true, always route to edit path.
        // This prevents "build me a login page" from wiping an existing app.
        const isNewBuild = mode !== "fix" && mode !== "explain" && !hasRealApp;

        console.warn("🔴🔴🔴 [useAICodeAgent] Build routing:", {
          mode,
          isScaffoldPlaceholder,
          hasPages,
          hasScreens,
          hasRealComponents,
          hasRealApp,
          isNewBuild,
          fileCount: currentFiles.length,
          appContentLength: appContent.length,
        });

        // 2026-05-01: Pattern 1 — fire thinking message immediately, before branch to build/edit path
        onStreamEvent("narrative", {
          text: isNewBuild ? "On it. Designing your app..." : "On it. Working on those changes...",
          step: "thinking"
        });

        if (isNewBuild) {
          // ═══════════════════════════════════════════════════════════════════════════
          // SINGLE-CALL ARCHITECTURE — Server-side streaming via stream-app-build
          // API key never leaves the server. Edge function proxies Anthropic stream.
          // ═══════════════════════════════════════════════════════════════════════════
          console.warn("🔵🔵🔵 CALLING STREAM-APP-BUILD NEW BUILD MODE");
          console.log("[useAICodeAgent] NEW BUILD PATH — calling stream-app-build");

          const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
          const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;

          if (!token) {
            throw new Error("No auth session — please sign in to build apps");
          }

          console.log("[useAICodeAgent] stream-app-build request:", {
            url: `${supabaseUrl}/functions/v1/stream-app-build`,
            projectId: projId,
            messagePreview: (opts?.overrideMessage || messageToSend).slice(0, 100),
            hasToken: !!token,
          });

          onStreamEvent("progress", { phase: "planning", message: "Planning your app..." });

          // Call stream-app-build which handles both context generation and Anthropic streaming
          const streamResponse = await fetch(`${supabaseUrl}/functions/v1/stream-app-build`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: anonKey,
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              projectId: projId,
              message: opts?.overrideMessage || messageToSend,
            }),
          });

          console.log("[useAICodeAgent] stream-app-build response:", streamResponse.status, streamResponse.ok);

          if (!streamResponse.ok) {
            const text = await streamResponse.text().catch(() => "");
            throw new Error(`Build stream failed: ${streamResponse.status} ${text.slice(0, 200)}`);
          }

          const reader = streamResponse.body?.getReader();
          if (!reader) throw new Error("No response body from stream-app-build");

          const decoder = new TextDecoder();
          let manifest: Array<{ path: string; type: string; description: string }> = [];
          let industry = "saas";
          let indexCss = "";
          let buffer = "";
          let buildMessage = "";
          let filesReceived = 0; // Counter for progress UI (like old filesCompleted)

          // Accumulate files as they stream in (no giant JSON parse needed)
          type PatchFile = { path: string; action: string; content: string };
          const patchFiles: PatchFile[] = [];

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const jsonStr = line.slice(6).trim();
              if (!jsonStr) continue;

              let event: Record<string, unknown>;
              try {
                event = JSON.parse(jsonStr);
              } catch (parseErr) {
                console.warn("[useAICodeAgent] Skipping malformed SSE JSON:", jsonStr.slice(0, 100));
                continue;
              }

              // DEBUG: Log ALL event types received
              console.log("[useAICodeAgent] SSE event received:", event.type, event.type === "file" ? (event as any).path : "");

              // Handle error events OUTSIDE try/catch so they propagate
              if (event.type === "error") {
                const errorMsg = (event.error || event.message || "Build failed") as string;
                console.error("[useAICodeAgent] stream-app-build error event:", errorMsg, event);
                throw new Error(`Build failed: ${errorMsg}`);
              }

              if (event.type === "context") {
                // Initial context with manifest
                manifest = (event.manifest || []) as typeof manifest;
                industry = (event.industry || "saas") as string;
                indexCss = (event.indexCss || "") as string;

                setStreamingProgress(prev => ({ ...prev, phase: "generating", totalExpectedFiles: manifest.length, lastUpdate: Date.now() }));
                setPlanPreview({
                  message: `Building ${manifest.length} files`,
                  industry,
                  totalFiles: manifest.length,
                  files: manifest,
                });

                if (buildSessionId) {
                  try {
                    localStorage.setItem(`dayone:sandbox:buildstate:${buildSessionId}`,
                      JSON.stringify({ phase: "generating", completed: 0, total: manifest.length, ts: Date.now() }));
                  } catch { /* ignore */ }
                }

                onStreamEvent("progress", { phase: "generating", message: "Generating your app..." });
                onStreamEvent("narrative", { text: `Building ${manifest.length} files for your ${industry} app...`, step: "generate" });
              }
              else if (event.type === "progress") {
                // Progress update from backend — always update counter from progress events
                // This ensures the UI shows file count even if individual file events have issues
                const filesGenerated = (event.filesGenerated || 0) as number;
                if (filesGenerated > 0) {
                  // Use real file data if available, otherwise use placeholders
                  const completedFilesData = filesGenerated <= patchFiles.length
                    ? patchFiles.slice(0, filesGenerated).map((f, i) => ({
                        path: f.path || `File ${i + 1}`,
                        action: f.action || "create",
                        size: f.content?.length ?? 0,
                        completedAt: Date.now(),
                        label: (f as any)?.label,
                      }))
                    : Array.from({ length: filesGenerated }, (_, i) => ({
                        path: patchFiles[i]?.path || `File ${i + 1}`,
                        action: patchFiles[i]?.action || "create",
                        size: patchFiles[i]?.content?.length ?? 0,
                        completedAt: Date.now(),
                        label: (patchFiles[i] as any)?.label,
                      }));
                  setStreamingProgress(prev => ({
                    ...prev,
                    lastUpdate: Date.now(),
                    completedFiles: completedFilesData,
                  }));
                }
                if (buildSessionId) {
                  try {
                    localStorage.setItem(`dayone:sandbox:buildstate:${buildSessionId}`,
                      JSON.stringify({ phase: "generating", completed: filesGenerated > 0 ? filesGenerated : filesReceived, total: manifest.length, ts: Date.now() }));
                  } catch { /* ignore */ }
                }
              }
              else if (event.type === "file") {
                // DEBUG: Raw log to verify event handler is called
                console.log("FILE EVENT HIT", event);
                // Individual file streamed from backend (no JSON parsing needed!)
                const file: PatchFile & { label?: string } = {
                  path: event.path as string,
                  action: (event.action || "create") as string,
                  content: (event.content || "") as string,
                  label: (event.label || undefined) as string | undefined,
                };
                patchFiles.push(file);
                filesReceived++;
                console.log("[useAICodeAgent] FILE EVENT RECEIVED:", {
                  path: file.path,
                  label: file.label,
                  contentLen: file.content.length,
                  filesReceived,
                  manifestLen: manifest.length,
                  patchFilesLen: patchFiles.length,
                });

                // Update progress with counter (same pattern as old delta handler)
                // lastUpdate forces React to see this as a new state even if object shape is similar
                setStreamingProgress(prev => ({
                  ...prev,
                  lastUpdate: Date.now(),
                  completedFiles: Array.from({ length: filesReceived }, (_, i) => ({
                    path: patchFiles[i]?.path || `File ${i + 1}`,
                    action: patchFiles[i]?.action || "create",
                    size: patchFiles[i]?.content?.length ?? 0,
                    completedAt: Date.now(),
                    label: (patchFiles[i] as any)?.label,
                  })),
                }));

                if (buildSessionId) {
                  try {
                    localStorage.setItem(`dayone:sandbox:buildstate:${buildSessionId}`,
                      JSON.stringify({ phase: "generating", completed: filesReceived, total: manifest.length, ts: Date.now() }));
                  } catch { /* ignore */ }
                }
              }
              else if (event.type === "complete") {
                // Build complete - immediately update progress to show final count
                buildMessage = (event.message || `Generated ${patchFiles.length} files`) as string;
                industry = (event.industry || industry) as string;
                console.log("[useAICodeAgent] Build complete:", buildMessage, "Files:", patchFiles.length);
                // Trigger immediate state update so UI shows completion
                setStreamingProgress(prev => ({
                  ...prev,
                  phase: "complete",
                  lastUpdate: Date.now(),
                  completedFiles: patchFiles.map((f, i) => ({
                    path: f.path || `File ${i + 1}`,
                    action: f.action || "create",
                    size: f.content?.length ?? 0,
                    completedAt: Date.now(),
                    label: (f as any)?.label,
                  })),
                }));
              }
              // Import validation event - shows validation results in progress
              else if (event.type === "validation") {
                const passed = event.passed as boolean;
                const errorsFixed = (event.errors_fixed || 0) as number;
                const passes = (event.passes || 0) as number;
                const remaining = (event.remaining_errors || 0) as number;
                console.log("[useAICodeAgent] Import validation:", { passed, errorsFixed, passes, remaining });

                // Update progress with validation status
                setStreamingProgress(prev => ({
                  ...prev,
                  lastUpdate: Date.now(),
                  validationPassed: passed,
                  validationErrorsFixed: errorsFixed,
                  validationPasses: passes,
                  validationRemaining: remaining,
                }));
              }
              // Legacy support for delta/done events (backwards compatibility)
              else if (event.type === "delta" || event.type === "done") {
                console.log("[useAICodeAgent] Legacy event type:", event.type);
              }
              // Warning events (e.g., truncation warnings)
              else if (event.type === "warning") {
                const warningType = (event.warningType || "unknown") as string;
                const warningMessage = (event.message || "Build warning") as string;
                const warningFiles = (event.files || []) as string[];
                console.warn(`[useAICodeAgent] Build warning (${warningType}):`, warningMessage, warningFiles);

                // Surface truncation warnings to user via toast
                if (warningType === "truncation" && warningFiles.length > 0) {
                  toast.warning(`${warningFiles.length} file(s) may be incomplete. Check the preview.`);
                }
              }
            }
          }

          // Add index.css with design system if not in patch
          if (!patchFiles.some(f => f.path === "src/index.css") && indexCss) {
            patchFiles.unshift({ path: "src/index.css", action: "create", content: indexCss });
          }

          // Update final progress
          console.log("[useAICodeAgent] STREAM LOOP ENDED:", {
            patchFilesLen: patchFiles.length,
            filesReceived,
            manifestLen: manifest.length,
          });
          const completedFiles = patchFiles.map((f) => ({
            path: f.path,
            action: f.action,
            size: f.content?.length ?? 0,
            completedAt: Date.now(),
          }));
          console.log("[useAICodeAgent] SETTING FINAL PROGRESS:", { completedFilesLen: completedFiles.length, totalExpectedFiles: manifest.length });
          setStreamingProgress({ phase: "complete", completedFiles, totalExpectedFiles: manifest.length, lastUpdate: Date.now() });

          if (buildSessionId) {
            try {
              localStorage.setItem(`dayone:sandbox:buildstate:${buildSessionId}`,
                JSON.stringify({ phase: "complete", completed: patchFiles.length, total: manifest.length, ts: Date.now() }));
            } catch { /* ignore */ }
          }

          // Signal completion to parent (triggers file tree refresh + preview reload)
          onStreamEvent("progress", { phase: "complete", message: `Generated ${patchFiles.length} files` });
          onStreamEvent("narrative", { text: `Build complete! ${patchFiles.length} files ready.`, step: "complete" });

          console.log("[useAICodeAgent] Final patch files:", patchFiles.length);
          data = {
            patch: patchFiles,
            message: buildMessage || `Generated ${patchFiles.length} files`,
            summary: buildMessage || `Generated ${patchFiles.length} files for your ${industry} app`,
          };

        } else {
          // ═══════════════════════════════════════════════════════════════════════════
          // EDIT PATH — Used for edits, fixes, and follow-up builds
          // Calls execute-build-plan orchestrator which:
          // 1. Proxies builder-chat-openai for file generation (SSE streaming)
          // 2. Applies files via code-workspace-files
          // 3. Applies migrations via apply-schema if detected
          // ═══════════════════════════════════════════════════════════════════════════
          console.warn("🟢🟢🟢 CALLING EXECUTE-BUILD-PLAN");
          console.log("[useAICodeAgent] EDIT PATH — calling execute-build-plan orchestrator");
          try {
            data = await invokeEdgeFunction("execute-build-plan", edgeFnBody, { onStreamEvent });
          } catch (firstErr) {
            const msg = firstErr instanceof Error ? firstErr.message : String(firstErr);
            // Silent retry once on malformed JSON errors
            if (/MODEL_INVALID_JSON|malformed|JSON.*parse|Unexpected token/i.test(msg)) {
              console.log("[useAICodeAgent] Malformed JSON — silent retry");
              toast.info("Retrying...");
              setStreamingProgress({ phase: "generating", completedFiles: [] });
              try {
                data = await invokeEdgeFunction("execute-build-plan", edgeFnBody, { onStreamEvent });
              } catch (retryErr) {
                throw retryErr; // Let outer catch handle it
              }
            } else {
              throw firstErr;
            }
          }
        }

        const meta = data?.meta as ServerPatchMeta | undefined;
        if (meta && Array.isArray(meta.rejectedPatchItems) && typeof meta.acceptedCount === "number") {
          setLastServerPatchMeta({
            rejectedPatchItems: meta.rejectedPatchItems,
            acceptedCount: meta.acceptedCount,
            rejectedCount: meta.rejectedCount ?? 0,
            patchTotalChars: meta.patchTotalChars ?? 0,
            ...(meta.intent && { intent: meta.intent }),
            ...(Array.isArray(meta.intentRejections) && { intentRejections: meta.intentRejections }),
            ...(typeof meta.intentRejectionsCount === "number" && { intentRejectionsCount: meta.intentRejectionsCount }),
            ...(typeof meta.allowCoreEdits === "boolean" && { allowCoreEdits: meta.allowCoreEdits }),
            ...(typeof meta.coreRejectedCount === "number" && { coreRejectedCount: meta.coreRejectedCount }),
          });
        } else {
          setLastServerPatchMeta(null);
        }

        if (mode === "explain") {
          const explanation = data.explanation as ExplanationResult;
          const assistantContent = explanation?.summary ?? "Explanation generated";

          const explainMsg: Message = {
            role: "assistant",
            content: assistantContent,
            explanation,
            mode,
            timestamp: new Date(),
          };
          pushMessage(explainMsg);
          persistMessage(explainMsg, projId);

          // Append to conversation history for multi-turn context
          conversationHistoryRef.current.push(
            { role: "user", content: userMessageContent },
            { role: "assistant", content: assistantContent },
          );

          return explanation;
        }

        let plan = patchToPlan(data);

        // [EDIT] Frontend Stage 1: Log patch received from API
        const isEditMode = !isNewBuild && files.length > 0;
        if (isEditMode) {
          console.log("[EDIT_FRONTEND] PATCH_RECEIVED:", {
            dataPatchLength: data?.patch?.length ?? 0,
            planEditsLength: plan.edits.length,
            patchPaths: plan.edits.map(e => e.filePath).join(", "),
            summary: plan.summary?.slice(0, 100),
          });
          if (plan.edits.length === 0 && (data?.patch?.length ?? 0) > 0) {
            console.error("[EDIT_FRONTEND] BUG: API returned patch but patchToPlan produced 0 edits!");
          }
          if (plan.edits.length === 0) {
            console.warn("[EDIT_FRONTEND] WARNING: No edits to apply - check server logs for [EDIT] prefix");
          }
        }
        // DEBUG: Log plan after patchToPlan to trace BUG 2
        console.log("[useAICodeAgent] patchToPlan result:", {
          dataHasPatch: Array.isArray(data?.patch),
          dataPatchLength: data?.patch?.length ?? 0,
          planEditsLength: plan.edits.length,
          planSummary: plan.summary?.slice(0, 100),
          isNewBuild,
        });

        // ── Pre-flight validation: syntax, imports, exports ──────────────
        // Invisible to user — auto-fix failing files before they ever see the plan.
        // SKIP for first builds (agentic): the agentic pipeline already has per-file
        // syntax checking, regeneration, and import validation. Calling builder-chat-openai
        // again with 40+ files causes 500s due to request size/timeout.
        // Note: isNewBuild is already defined above in the batched pipeline section
        if (plan.edits.length > 0 && !isNewBuild) {
          // Step 1: Quick client-side import fix (rewrites dangling imports to closest match)
          const importFix = autoFixImports(plan.edits);
          if (importFix.fixCount > 0) {
            plan = { ...plan, edits: importFix.edits };
            console.log(`[preflight] autoFixImports corrected ${importFix.fixCount} import(s)`);
          }

          // Step 2: Full validation (syntax, remaining imports, exports)
          const existingFileMap = new Map(files.map((f) => [f.name, f.content]));
          const preflight = await runPreflightValidation(plan.edits, existingFileMap);

          if (!preflight.passed) {
            console.log("[preflight] Validation failed, attempting auto-fix:", preflight.issues);

            // Build a fix instruction and call the builder again
            const fixInstruction = buildFixInstruction(preflight.issues);
            const failingPaths = new Set(preflight.issues.map((i: ValidationIssue) => i.filePath));

            // Include current generated files as context so the AI can see the full set
            const currentFilesForFix = [
              ...files.map((f) => ({ path: f.name, content: f.content })),
              ...plan.edits
                .filter((e) => e.action !== "delete" && e.content != null)
                .map((e) => ({ path: e.filePath, content: e.content! })),
            ];

            try {
              // Phase F.4b: Send errorLogs so isFixRequest=true triggers in backend,
              // which activates Phase G3's bugfix smart_target with error path extraction
              const errorLogs = preflight.issues
                .map((i: ValidationIssue) => `${i.filePath}: [${i.type}] ${i.message}`)
                .join('\n');

              // Route fix calls through orchestrator so migration fixes are also applied
              const fixData = await invokeEdgeFunction("execute-build-plan", {
                projectId: projId,
                message: fixInstruction,
                currentFiles: currentFilesForFix,
                errorLogs,  // NEW: triggers isFixRequest in backend
                mode: "fix",
              });

              const fixPlan = patchToPlan(fixData);

              if (fixPlan.edits.length > 0) {
                // Merge: replace failing files with fixed versions, keep passing files unchanged
                const fixedMap = new Map(fixPlan.edits.map((e) => [e.filePath, e]));
                const mergedEdits = plan.edits.map((edit) =>
                  fixedMap.has(edit.filePath) ? fixedMap.get(edit.filePath)! : edit
                );
                // Also include any new files the fix added
                for (const fixEdit of fixPlan.edits) {
                  if (!plan.edits.some((e) => e.filePath === fixEdit.filePath)) {
                    mergedEdits.push(fixEdit);
                  }
                }
                plan = { ...plan, edits: mergedEdits };
                console.log("[preflight] Auto-fix applied for:", [...failingPaths].join(", "));
              }
            } catch (fixErr) {
              // Fix attempt failed — proceed with original plan (user will see errors at build time)
              console.warn("[preflight] Auto-fix failed, proceeding with original:", fixErr);
              const fixErrMsg = fixErr instanceof Error ? fixErr.message : String(fixErr);
              logBuildErrorClient({
                projectId: projId,
                errorClass: "auto_fix_timeout",
                errorMessage: `Auto-fix failed: ${fixErrMsg}`,
                errorStack: fixErr instanceof Error ? fixErr.stack : undefined,
                recovered: true, // We proceed with original plan
              });
            }
          }
        } else if (plan.edits.length > 0) {
          // First builds: only run client-side import fix (no API call)
          const importFix = autoFixImports(plan.edits);
          if (importFix.fixCount > 0) {
            plan = { ...plan, edits: importFix.edits };
            console.log(`[preflight] autoFixImports corrected ${importFix.fixCount} import(s) (first build, skipped server preflight)`);
          }
        }

        if (plan.edits.length === 0) {
          toast.info("No code changes generated. Try a more specific request.");
          const summary = (data as { assistantMessage?: string; summary?: string })?.assistantMessage ?? (data as { summary?: string })?.summary ?? "No changes generated.";
          setCurrentPlan(plan);
          const noChangeMsg: Message = {
            role: "assistant",
            content: summary,
            editPlan: plan,
            mode,
            timestamp: new Date(),
          };
          pushMessage(noChangeMsg);
          persistMessage(noChangeMsg, projId);
          await addPromptToHistory(instruction, summary);

          // Append to conversation history even for empty results (AI knows it couldn't act)
          conversationHistoryRef.current.push(
            { role: "user", content: userMessageContent },
            { role: "assistant", content: summary },
          );

          return plan;
        }

        // ✅ Inject index.html + App + main if missing (but not if project already has them)
        const existingFilePaths = new Set(files.map((f) => f.name));
        plan = ensurePreviewFilesInPlan(plan, existingFilePaths);

        if (opts?.filterForLanding && Array.isArray(plan.edits)) {
          const { filteredEdits, droppedEdits } = filterEditsForLanding(plan.edits);
          plan = { ...plan, edits: filteredEdits };
          if (droppedEdits.length > 0) {
            const sample = droppedEdits.slice(0, 5).map((d) => d.path);
            opts.onLandingFilterDropped?.(droppedEdits.length, sample);
          }
        }

        // ── Security scan: detect hardcoded secrets/API keys ──────────────────
        const filesToScan = plan.edits
          .filter((e) => e.action !== "delete" && e.content != null)
          .map((e) => ({ name: e.filePath, content: e.content! }));

        if (filesToScan.length > 0) {
          const scanResult = scanForSecrets(filesToScan);
          if (scanResult.hasSecrets) {
            console.warn("[security] Detected secrets in generated code:", scanResult.summary);
            setSecurityAlerts(scanResult.matches);
            // Show toast for critical secrets
            const criticalCount = scanResult.matches.filter((m) => m.severity === "critical").length;
            if (criticalCount > 0) {
              toast.warning(`Security: ${criticalCount} hardcoded secret${criticalCount !== 1 ? "s" : ""} detected. Review before deploying.`);
            }
          } else {
            setSecurityAlerts([]);
          }

          // ── RLS validation: check database migrations have Row Level Security ──
          const rlsValidation = validateMigrationFiles(filesToScan);
          if (!rlsValidation.passed) {
            const missingRLS = rlsValidation.results.flatMap((r) =>
              r.validation.tables.filter((t) => !t.hasRLS).map((t) => t.name)
            );
            if (missingRLS.length > 0) {
              console.warn("[rls] Tables missing RLS:", missingRLS.join(", "));
              toast.warning(`Security: ${missingRLS.length} table${missingRLS.length !== 1 ? "s" : ""} missing Row Level Security`);
            }
          }
        }

        setCurrentPlan(plan);

        const patchPaths = plan.edits.map((e) => e.filePath);
        const buildMsg: Message = {
          role: "assistant",
          content: plan.summary,
          editPlan: plan,
          mode,
          timestamp: new Date(),
        };
        pushMessage(buildMsg);
        persistMessage(buildMsg, projId, patchPaths);

        await addPromptToHistory(instruction, plan.summary);

        // Update project memory with app metadata after successful build
        if (!memory?.app_purpose && instruction.length > 10) {
          // First build: set app purpose from the user's initial prompt
          await setAppPurpose(instruction.slice(0, 500));
        }

        // Detect and save tech stack from generated files
        const techStackUpdates: Record<string, string> = {};
        if (patchPaths.some(p => p.includes("/components/ui/"))) {
          techStackUpdates.ui = "shadcn/ui components";
        }
        if (patchPaths.some(p => p.includes("supabase"))) {
          techStackUpdates.backend = "Supabase";
        }
        if (patchPaths.some(p => p.includes("AuthContext") || p.includes("/auth/"))) {
          techStackUpdates.authentication = "Supabase Auth";
        }
        if (Object.keys(techStackUpdates).length > 0) {
          await updateTechStack(techStackUpdates);
        }

        // Add a feature entry for substantial changes (5+ files)
        if (patchPaths.length >= 5 && plan.summary) {
          const featureName = plan.summary.split(/[.!]/)[0].slice(0, 100) || "New feature";
          await addFeature(featureName, `${patchPaths.length} files changed`);
        }

        // Append to conversation history: user message + concise assistant summary (not full file contents)
        const assistantHistoryContent = `${plan.summary}\n\nFiles changed: ${patchPaths.join(", ")}`;
        conversationHistoryRef.current.push(
          { role: "user", content: userMessageContent },
          { role: "assistant", content: assistantHistoryContent },
        );

        return plan;
      } catch (err) {
        const e = normalizeError(err);
        const cause = (err instanceof Error ? (err as any).cause : null) as { status?: number; text?: string; url?: string } | null;
        console.error("[BUILD_ERROR]", {
          status: cause?.status ?? "unknown",
          body: (cause?.text ?? e.message ?? "").slice(0, 500),
          message: e.message,
          stack: e.stack,
          url: cause?.url,
        });

        // Telemetry: log build failure
        logBuildErrorClient({
          projectId: projId,
          errorClass: classifyBuildError(e.message),
          errorMessage: e.message,
          errorStack: e.stack,
        });

        toast.error(friendlyErrorMessage(e.message));

        const errorMsg: Message = {
          role: "assistant",
          content: friendlyErrorMessage(e.message),
          mode,
          timestamp: new Date(),
        };
        pushMessage(errorMsg);
        persistMessage(errorMsg, projId);

        return null;
      } finally {
        setIsLoading(false);
        // Keep completedFiles so counter shows final count; only reset phase to idle
        setStreamingProgress(prev => prev.phase !== "idle" ? { ...prev, phase: "idle" } : prev);
        // Clear build state from localStorage so __render.tsx stops showing skeleton
        if (buildSessionId) {
          try { localStorage.removeItem(`dayone:sandbox:buildstate:${buildSessionId}`); } catch { /* ignore */ }
        }
      }
    },
    [memory, addPromptToHistory, setAppPurpose, updateTechStack, addFeature, buildContextString, ensurePreviewFilesInPlan, pushMessage, persistMessage]
  );

  const clearConversation = useCallback(() => {
    messagesRef.current = [];
    setMessages([]);
    setCurrentPlan(null);
    conversationHistoryRef.current = [];
  }, []);

  const dismissLastPlan = useCallback(() => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.editPlan) {
        next[next.length - 1] = { ...last, editPlan: undefined };
        messagesRef.current = next;
      }
      return next;
    });
    setCurrentPlan(null);
  }, []);

  const clearSecurityAlerts = useCallback(() => {
    setSecurityAlerts([]);
  }, []);

  return {
    messages,
    isLoading,
    currentPlan,
    currentMode,
    setCurrentMode,
    sendInstruction,
    clearConversation,
    dismissLastPlan,
    pushMessage,
    memory,
    isMemoryLoading,
    setAppPurpose,
    addFeature,
    addArchitectureDecision,
    updateTechStack,
    lastServerPatchMeta,
    lastRequestFlags,
    streamingProgress,
    planPreview,
    securityAlerts,
    clearSecurityAlerts,
    narrativeMessages,
  };
}
